import { getHotelsData } from '@/app/api/hotels'
import {
	DestPagesProps,
	HotelsDataType,
	HotelsListResponse,
	PaginatedHotelsResponse,
	QueryPageParam,
	UserData,
} from '@/app/types'
import { useInfiniteQuery } from '@tanstack/react-query'
import { omit } from 'lodash'

interface UseSimilarHotelsQueryProps {
	props: DestPagesProps
	country: string
	region: string
	city: string
	s?: string
	hotelID?: string
	placeID?: string
	useSimilarStarsFilter: boolean
	shouldFetchSimilarData: boolean
	data: HotelsListResponse
	startTimeRef: React.MutableRefObject<number>
	setIsSimilarFetchingComplete: (value: boolean) => void
	userData: UserData | null
	mapQueryKey?: string
}

export const useSimilarHotelsQuery = ({
	props,
	country,
	region,
	city,
	s,
	hotelID,
	placeID,
	useSimilarStarsFilter,
	shouldFetchSimilarData,
	data,
	startTimeRef,
	setIsSimilarFetchingComplete,
	userData,
	mapQueryKey,
}: UseSimilarHotelsQueryProps) => {
	const similarQueryKey = [
		'similarHotels',
		country,
		region,
		city,
		useSimilarStarsFilter ? s : null,
		hotelID,
		placeID,
		mapQueryKey ?? undefined,
	].filter((key): key is string => key !== undefined)

	return useInfiniteQuery<
		PaginatedHotelsResponse,
		Error,
		HotelsDataType,
		(string | boolean)[],
		QueryPageParam
	>({
		queryKey: similarQueryKey,
		queryFn: ({ pageParam = { page: 1, offset: 0 } }) =>
			getHotelsData(
				{
					...props,
					params: { ...props.params, dest: [country, region, city] },
					searchParams: {
						...omit(props.searchParams, [
							'entityKey',
							's',
							'gr',
							'mn',
							'mx',
							'fa',
							'p',
							't',
							'room',
							'ap',
						]),
						room: ['2'],
						entityKey: placeID && `kplace:${placeID}`,
						userAgent: userData?.userAgent,
						sessionID: userData?.sessionID,
						// If no currency in URL, use local currency from userData
						currency:
							props.searchParams.currency ||
							userData?.defaultCurrency?.abbreviation,
						// Only include stars if we're using the filter
						...(useSimilarStarsFilter && {
							s: props.searchParams.s || '',
						}),
						...(mapQueryKey ? { polygon: mapQueryKey } : {}),
						page: String(pageParam.page),
					},
				},
				false,
				hotelID ? [hotelID] : undefined
			).then((response) => {
				return {
					...response,
					page: pageParam.page,
					offset: pageParam.offset,
				}
			}),
		getNextPageParam: (lastPage, allPages) => {
			if (lastPage.page < lastPage.pages) {
				return {
					page: lastPage.page + 1,
					offset: lastPage?.offset
						? lastPage?.offset + lastPage.hotels.length
						: 0,
				}
			}
			return undefined
		},
		refetchInterval: (queryInfo) => {
			if (
				queryInfo.state.data?.pages[queryInfo.state.data.pages.length - 1]
					?.complete
			) {
				setIsSimilarFetchingComplete(true)
				return false
			}

			const timeElapsed = Date.now() - startTimeRef.current
			if (timeElapsed > 20000) {
				setIsSimilarFetchingComplete(true)
				return false
			}

			return 600
		},
		initialPageParam: { page: 1, offset: 0 },
		enabled: !!userData && shouldFetchSimilarData /* && data.hotels.length > 0 */,
	})
}
